import { MoteState } from '../models';
import { JourneyType } from '../components/transportation/transportation.model';

// Colors for map
export const mapColors = {
    motes: "#333333",  // Gray
    selectedMote: "#0074D9",	// e8c15f
    locked: "#e8c15f", 	// Gray = moteState.LOCKED
    unlocked: "#e8c15f", 	// Green = moteState.UNLOCKED
    available: "#dd4b39", 	// Gold = moteState.AVAILABLE
    userMote: '#0074D9', 	// Blue = moteState.USERMOTE
    routeInactive: "#5F86E8",
    routeColor: "#5F86E8",
    directions: "#0074D9",
    train: "#970d01",
    bus: "#FCA311",
    walk: "#12426e",
    ferry: "#EEEEEE",
    //routeColor:		"#dd4b39"   // Gray "#dd4b39" #e8c15f
};

// Standard circleRadius
export const circleRadius: number = 12;

// Standard filter
export const moteFilter = (id: string, filterCriteria: Array<string | MoteState> = null, circleColor: string = "#eee", strokeColor: string = "#555555", strokeWidth: number = 1, circleOpacity: number = .8) => {
    return {
        "id": id,
        "type": "circle",
        "source": "motes",
        "paint": {

            "circle-radius": circleRadius,
            "circle-color": circleColor,
            "circle-opacity": circleOpacity,
            "circle-stroke-width": strokeWidth,
            "circle-stroke-color": strokeColor
        },
        "filter": filterCriteria || ["==", "moteKey", ""]
    };
};

// Constant to generate layers
export const layers = {
    routes: {
        "id": "routes",
        "type": "line",
        "source": "routes",
        "layout": {
            "line-join": "round",
            "line-cap": "round"
        },
        "paint": {
            "line-color": ['match', ['get', 'moteState'], MoteState.INACTIVE, mapColors.routeInactive, mapColors.routeColor],
            "line-width": ['match', ['get', 'moteState'], MoteState.INACTIVE, 4, 4]
        }
    },
    routesClick: {
        "id": "routesClick",
        "type": "line",
        "source": "routes",
        "layout": {
            "line-join": "round",
            "line-cap": "round"
        },
        "paint": {
            "line-color": "#EEEEEE",
            "line-opacity": 0,
            "line-width": 20
        }
    },
    selectedRoute: {
        "id": "selectedRoute",
        "type": "line",
        "source": "routes",
        "layout": {
            "line-join": "round",
            "line-cap": "round"
        },
        "paint": {
            "line-color": mapColors.selectedMote,
            "line-width": 6
        },
        "filter": ["==", "moteState", ""]
    },
    directions: {
        "id": "directions",
        "type": "line",
        "source": "directions",
        "filter": ["!=", "routeType", JourneyType.WALK],
        "layout": {
            "line-join": "round",
            "line-cap": "round"
        },
        "paint": {
            "line-color": [
                'match',
                ['get', 'routeType'],
                JourneyType.WALK, mapColors.walk,
                JourneyType.BUS, mapColors.bus,
                JourneyType.EXTRA_BUS, mapColors.bus,
                JourneyType.NIGHT_BUS, mapColors.bus,
                JourneyType.TELE_BUS, mapColors.bus,
                JourneyType.S_TRAIN, mapColors.train,
                JourneyType.IC_TRAIN, mapColors.train,
                JourneyType.FAST_TRAIN, mapColors.train,
                JourneyType.REG_TRAIN, mapColors.train,
                JourneyType.METRO, mapColors.train,
                JourneyType.FERRY, mapColors.ferry,
                mapColors.directions // Other
            ],
            "line-width": 4
        }
    },
    directionsDottedBorder: {
        "id": "directionsDottedBorder",
        "type": "line",
        "source": "directions",
        "filter": ["==", "routeType", JourneyType.WALK],
        "layout": {
            "line-join": "round",
            "line-cap": "round"
        },
        "paint": {
            "line-color": mapColors.routeColor,
            "line-width": 8,
            "line-dasharray": [.1, 2]
        }
    },
    directionsDotted: {
        "id": "directionsDotted",
        "type": "line",
        "source": "directions",
        "filter": ["==", "routeType", JourneyType.WALK],
        "layout": {
            "line-join": "round",
            "line-cap": "round"
        },
        "paint": {
            "line-color": "#ffffff",
            "line-width": 4,
            "line-dasharray": [.2, 4]
        }
    },
    directionsFeature: {
        id: "directionsFeature",
        source: "directionsFeature",
        filter: ["!=", "isEndPoint", true],
        type: "circle",
        paint: {
            "circle-color": [
                'match',
                ['get', 'journeyType'],
                JourneyType.WALK, mapColors.walk,
                JourneyType.BUS, mapColors.bus,
                JourneyType.EXTRA_BUS, mapColors.bus,
                JourneyType.S_TRAIN, mapColors.train,
                JourneyType.FAST_TRAIN, mapColors.train,
                JourneyType.REG_TRAIN, mapColors.train,
                JourneyType.METRO, mapColors.train,
                mapColors.directions // Other
            ],
            "circle-opacity": 1,
            "circle-radius": [
                'interpolate', ['linear'], ['zoom'],
                0, ['case', ['get', 'isStartPoint'], 5, 0],
                //12, ['case', ['get', 'isStartPoint'], 8,  1 ],
                14, ['case', ['get', 'isStartPoint'], 5, 2],
                15, ['case', ['get', 'isStartPoint'], 20, 10]
                // 16, ['case', ['get', 'isStartPoint'], 20, 10 ],
                // 20, ['case', ['get', 'isStartPoint'], 30, 10 ],

            ],
            "circle-stroke-width": [
                'interpolate', ['linear'], ['zoom'],
                0, ['case', ['get', 'isStartPoint'], 0, 0],
                14, ['case', ['get', 'isStartPoint'], 0, 0],
                15, ['case', ['get', 'isStartPoint'], 2, 2]
            ],
            "circle-stroke-color": "#eeeeee",
        },
    },
    directionsText: {
        "id": "directionsText",
        "type": "symbol",
        "filter": ["==", "isStartPoint", true],
        "source": "directionsFeature",
        "layout": {
            "text-field": "{label}",
            "text-size": {
                type: "interval",
                stops: [
                    [0, 0],
                    [15, 15]
                    // [14, 18],
                    // [20, 20],
                ]
            },
            'text-justify': 'center'
        },
        "paint": {
            "text-color": "#FFFFFF"
        }
    },
    searchResults: {
        "id": "searchResults",
        "type": "symbol",
        "source": "searchResults",
        // "paint": {
        //     "circle-color": '#eee',
        //     "circle-radius": 10
        // }
        "layout": {
            "icon-image": "default_pin_icon",
            "icon-size": 0.25,
            "icon-allow-overlap": true,
            "icon-anchor": "bottom"
        }
    },
    motes: {
        "id": "motes",
        "type": "circle",
        "source": "motes",
        filter: ["!has", "point_count"],
        paint: {
            "circle-color": '#3b3b3b',
            "circle-radius": 9,
        }
    },
    moteClickLayer: {
        "id": "moteClickLayer",
        "type": "circle",
        "source": "motes",
        "paint": {
            "circle-opacity": 0,
            "circle-radius": circleRadius + 10,
        }
    },
    tourClickLayer: {
        id: "tourClickLayer",
        type: "circle",
        source: "tours",
        paint: {
            "circle-color": 'transparent',
            "circle-radius": 45
        }
    },
    storyClickLayer: {
        id: "storyClickLayer",
        type: "circle",
        source: "stories",
        paint: {
            "circle-color": 'transparent',
            "circle-radius": 23,
        }
    },
    clusters: {
        id: "clusters",
        type: "circle",
        source: "motes",
        filter: ["has", "point_count"],
        paint: {
            "circle-color": mapColors.motes,
            "circle-opacity": 1,
            "circle-stroke-width": 4,
            "circle-stroke-color": "#eeeeee",
            "circle-radius": {
                property: "point_count", // Tell Mapbox to add point_count property
                type: "interval",
                stops: [
                    [0, circleRadius],
                    [2, circleRadius + 3],
                    [3, circleRadius + 4],
                    [4, circleRadius + 5],
                    [5, circleRadius + 6],
                    [10, circleRadius + 8],
                    [15, circleRadius + 9],
                    [20, circleRadius + 13]
                ]
            }
        }
    },
    clusterCount: {
        "id": "clusterCount",
        "type": "symbol",
        "source": "motes",
        "layout": {
            "text-field": "{point_count}",
            "text-size": 16
        },
        "paint": {
            "text-color": "#eeeeee"
        }
    },
    click: {
        "id": "click",
        "type": "circle",
        "source": "click",
        "paint": {
            "circle-opacity": .25,
            "circle-color": '#eee',
            "circle-radius": 0
        }
    },
    layer3D: {
        'id': 'layer3D',
        'source': 'composite',
        'source-layer': 'building',
        'filter': ['==', 'extrude', 'true'],
        'type': 'fill-extrusion',
        'minzoom': 13,
        'paint': {
            'fill-extrusion-color': '#aaa',
            // add a smooth transition effect to the buildings as the user zooms in
            'fill-extrusion-height': [
                'interpolate', ['linear'], ['zoom'], 16, 0, 17.05, ['get', 'height']
            ],
            'fill-extrusion-base': [
                'interpolate', ['linear'], ['zoom'], 16, 0, 17.05, ['get', 'min_height']
            ],
            'fill-extrusion-opacity': 0.6
        }
    },
    selectedMote: moteFilter('selectedMote', null, mapColors.selectedMote, mapColors.selectedMote, 5, .5),
    selectedMoteUnder: moteFilter('selectedMoteUnder', null, mapColors.selectedMote, mapColors.selectedMote, 2, .5),
    availableMotesAnimation: moteFilter('availableMotesAnimation', ["==", "moteState", MoteState.AVAILABLE], 'transparent', mapColors.available, 0)
};
